import { ProductVM, OrderVM, OrderLineVM, OrderStatus, CategoryVM, InventoryVM } from "./types";
import { API_BASE_URL } from "../../Services/api_url";
import { inferCategory } from "./inferCategory";

const STATUS_MAP: Record<string, OrderStatus> = {
  new: "New", pending: "Pending", confirmed: "Confirmed",
  preparing: "Preparing", processing: "Preparing",
  delivered: "Delivered", shipped: "Delivered",
  cancelled: "Cancelled", canceled: "Cancelled",
};

export function normStatus(raw: any): OrderStatus {
  const key = String(raw ?? "").trim().toLowerCase();
  return STATUS_MAP[key] ?? (raw ? (String(raw) as OrderStatus) : "New");
}

/** Derive a readable packaging unit when the backend doesn't supply one. */
function derivePackaging(p: any): string {
  if (p?.packagingUnit) return p.packagingUnit;
  if (p?.unit) return p.unit;
  return "Case";
}

function deriveSku(p: any): string {
  if (p?.sku) return String(p.sku);
  const cat = String(p?.category ?? "GEN").slice(0, 3).toUpperCase();
  const id = p?.id ?? 0;
  return `${cat}-${String(1000 + Number(id))}`;
}

/** Pull an image from any of the field names this backend might use, and resolve
 *  relative paths against the API host so bare filenames / "/images/x" also load. */
function resolveImage(p: any): string | undefined {
  const raw =
    p?.image ?? p?.imageUrl ?? p?.imageurl ?? p?.imageURL ?? p?.image_url ??
    p?.imageLink ?? p?.imagePath ?? p?.img ?? p?.picture ?? p?.photo ?? p?.thumbnail;
  if (raw == null) return undefined;
  const v = String(raw).trim();
  if (!v) return undefined;
  if (/^(https?:|data:)/i.test(v)) return v;             // absolute URL / data URI
  if (v.startsWith("//")) return `https:${v}`;           // protocol-relative
  if (v.startsWith("/")) return `${API_BASE_URL}${v}`;   // server-relative path
  // Raw base64 image bytes stored without a data: prefix -> wrap them.
  if (/^[A-Za-z0-9+/=\r\n]+$/.test(v) && v.replace(/\s/g, "").length > 100) {
    const sig = v.slice(0, 8);
    const mime = sig.startsWith("/9j/") ? "image/jpeg"
      : sig.startsWith("iVBOR") ? "image/png"
      : sig.startsWith("R0lGOD") ? "image/gif"
      : sig.startsWith("UklGR") ? "image/webp"
      : "image/*";
    return `data:${mime};base64,${v.replace(/\s/g, "")}`;
  }
  // Bare filename -> served by the backend's uploads folder (QA #3 image store).
  return `${API_BASE_URL}/uploads/${v.replace(/^\/+/, "")}`;
}

/** Products may carry their category as a nested object, a plain name, or just an id. */
function extractCategory(p: any): { name?: string; id?: number } {
  const c = p?.category;
  if (c && typeof c === "object") {
    const id = Number(c.categoryid ?? c.id);
    return { name: c.catname ?? c.name ?? undefined, id: Number.isFinite(id) ? id : undefined };
  }
  const idRaw = p?.categoryid ?? p?.categoryId ?? p?.category_id ?? (typeof c === "number" ? c : undefined);
  const id = Number(idRaw);
  const name = typeof c === "string" && c.trim() ? c : (p?.categoryName ?? p?.catname ?? undefined);
  return { name: name || undefined, id: Number.isFinite(id) ? id : undefined };
}

export function toProductVM(p: any): ProductVM {
  const id = Number(p?.id ?? p?.productId ?? 0);
  const name = String(p?.name ?? "Unnamed product");
  const cat = extractCategory(p);
  const category = cat.name || inferCategory(name);       // backend category wins, else inferred
  const realImage = resolveImage(p);                      // only a genuine http/data URL
  return {
    id,
    name,
    description: p?.description ?? "",
    price: Number(p?.price ?? p?.basePrice ?? 0),
    stock: Number(p?.stock ?? p?.quantityavailable ?? p?.quantityAvailable ?? 0),
    category,
    categoryId: cat.id,
    vendor: p?.vendor != null ? String(p.vendor) : undefined,
    image: realImage, // real/tagged image only; caller shows a deterministic tile when absent
    sku: deriveSku(p),
    packagingUnit: derivePackaging(p),
    unitsPerCase: p?.unitsPerCase != null ? Number(p.unitsPerCase) : undefined,
  };
}

export function toOrderVM(o: any): OrderVM {
  const rawId = o?.orderId ?? o?.ordersId ?? o?.id ?? "";
  const display = String(rawId).toUpperCase().startsWith("ORD")
    ? String(rawId)
    : `ORD-${rawId}`;
  const rawLines: any[] = Array.isArray(o?.orderItems) ? o.orderItems
    : Array.isArray(o?.items) ? o.items : [];
  const lines: OrderLineVM[] = rawLines.map((li) => {
    const quantity = Number(li?.quantity ?? 0);
    const unitPrice = Number(li?.unitPrice ?? li?.price ?? 0);
    return {
      productId: li?.productId != null ? Number(li.productId) : undefined,
      productName: String(li?.productName ?? li?.name ?? "Item"),
      sku: li?.sku != null ? String(li.sku) : undefined,
      packagingUnit: li?.packagingUnit != null ? String(li.packagingUnit) : undefined,
      quantity,
      unitPrice,
      lineTotal: Number(li?.lineTotal ?? quantity * unitPrice),
    };
  });
  const itemCount = typeof o?.itemCount === "number" ? o.itemCount
    : typeof o?.items === "number" ? o.items
    : lines.length;
  return {
    id: Number(o?.id ?? 0),
    orderId: display,
    customer: String(o?.customerName ?? o?.customer ?? o?.userName ?? o?.userId ?? "—"),
    userId: o?.userId != null ? Number(o.userId) : undefined,
    distributorId: o?.distributorId != null ? Number(o.distributorId) : undefined,
    items: Number(itemCount),
    orderDate: o?.orderDate ?? undefined,
    deliveryDate: o?.deliveryDate ?? undefined,
    subtotal: o?.subtotal != null ? Number(o.subtotal) : undefined,
    tax: o?.tax != null ? Number(o.tax) : undefined,
    total: Number(o?.totalAmount ?? o?.total ?? 0),
    status: normStatus(o?.status),
    lines,
    internalNotes: o?.internalNotes != null ? String(o.internalNotes) : undefined,
    statusHistory: Array.isArray(o?.statusHistory)
      ? o.statusHistory.map((h: any) => ({
          fromStatus: h?.fromStatus ?? undefined,
          toStatus: h?.toStatus ?? undefined,
          changedAt: h?.changedAt ?? undefined,
          note: h?.note ?? undefined,
        }))
      : undefined,
  };
}

export function toCategoryVM(c: any): CategoryVM {
  return {
    id: Number(c?.id ?? c?.categoryid ?? 0),
    name: String(c?.catname ?? c?.name ?? "Category"),
    image: c?.image ?? null,
    parentId: c?.parentId ?? null,
  };
}

export function toInventoryVM(i: any): InventoryVM {
  return {
    product: String(i?.product ?? i?.name ?? "—"),
    quantityAvailable: Number(i?.quantityavailable ?? i?.quantityAvailable ?? 0),
  };
}
